const QUICK_ADD_ROOT_ID = 'quick-add-dialog';
const QUICK_ADD_CONTENT_ID = 'quick-add-modal-content';
const SIZE_GUIDE_ID = 'VelzeenSizeGuide';
const SIZE_GUIDE_THEME_VARS = {
  '--vz-product-ink': '#2d2721',
  '--vz-product-soft': '#62584f',
  '--vz-product-paper': '#f7f3ea',
  '--vz-ease': 'cubic-bezier(0.2, 0.7, 0, 1)',
};

let pendingGuideUrl = '';
let pendingGuidePromise = null;
let activeGuideSession = null;

function getQuickAddRoot() {
  return document.getElementById(QUICK_ADD_ROOT_ID);
}

function getQuickAddProductUrl(root) {
  const picker = root?.querySelector(`#${QUICK_ADD_CONTENT_ID} variant-picker[data-product-url]`);
  const value = picker?.dataset.productUrl;
  if (!value) return null;

  const url = new URL(value, window.location.origin);
  return url.origin === window.location.origin ? url : null;
}

async function fetchProductSizeGuide(productUrl) {
  if (pendingGuidePromise && pendingGuideUrl === productUrl.href) return pendingGuidePromise;

  pendingGuideUrl = productUrl.href;
  pendingGuidePromise = fetch(productUrl.href, {
    credentials: 'same-origin',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  })
    .then((response) => {
      if (!response.ok) throw new Error(`Size chart request failed (${response.status}).`);
      return response.text();
    })
    .then((htmlText) => {
      const html = new DOMParser().parseFromString(htmlText, 'text/html');
      const source = html.querySelector(`#${SIZE_GUIDE_ID}`);
      if (!(source instanceof HTMLDialogElement)) throw new Error('Product size chart was not found.');

      const guide = document.importNode(source, true);
      Object.entries(SIZE_GUIDE_THEME_VARS).forEach(([property, value]) => {
        guide.style.setProperty(property, value);
      });
      guide.style.backgroundColor = '#f7f3ea';
      guide.style.color = '#2d2721';
      guide.dataset.vzQuickAddSharedGuide = '';
      guide.dataset.productUrl = productUrl.href;
      return guide;
    })
    .finally(() => {
      pendingGuidePromise = null;
      pendingGuideUrl = '';
    });

  return pendingGuidePromise;
}

function reportGuideError(root) {
  const status = root.querySelector(`#${QUICK_ADD_CONTENT_ID} [data-purchase-status]`);
  if (!(status instanceof HTMLElement)) return;

  status.textContent = '';
  requestAnimationFrame(() => {
    status.textContent = 'Size chart could not be loaded. Please open the product page.';
  });
}

function releaseActiveGuide() {
  activeGuideSession?.cleanup();
}

function mountProductSizeGuide(root, guide) {
  releaseActiveGuide();

  const originalGuide = document.getElementById(SIZE_GUIDE_ID);
  const placeholder = originalGuide ? document.createComment('Velzeen product size guide') : null;

  if (originalGuide instanceof HTMLDialogElement && originalGuide.open) originalGuide.close();
  if (originalGuide && placeholder) originalGuide.replaceWith(placeholder);

  root.appendChild(guide);

  const session = {
    guide,
    originalGuide,
    placeholder,
    cleaning: false,
    cleanup() {
      if (session.cleaning) return;
      session.cleaning = true;

      if (guide.open) guide.close();
      guide.remove();

      if (originalGuide && placeholder?.isConnected) placeholder.replaceWith(originalGuide);
      else placeholder?.remove();

      if (activeGuideSession === session) activeGuideSession = null;
    },
  };

  guide.addEventListener('close', session.cleanup, { once: true });
  activeGuideSession = session;
  return guide;
}

async function openProductSizeGuide(root, control) {
  const productUrl = getQuickAddProductUrl(root);
  if (!productUrl) {
    reportGuideError(root);
    return;
  }

  control.setAttribute('aria-controls', SIZE_GUIDE_ID);
  control.setAttribute('aria-busy', 'true');

  try {
    const guide = await fetchProductSizeGuide(productUrl);
    const mountedGuide = mountProductSizeGuide(root, guide);
    mountedGuide.showModal();
  } catch (error) {
    console.error(error);
    reportGuideError(root);
  } finally {
    control.removeAttribute('aria-busy');
  }
}

function bindQuickAddSizeGuide() {
  const root = getQuickAddRoot();
  if (!(root instanceof HTMLElement) || root.dataset.vzSizeGuideBound === 'true') return;
  root.dataset.vzSizeGuideBound = 'true';

  root.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      const openControl = event.target.closest('[data-vz-size-open]');
      if (openControl && openControl.closest(`#${QUICK_ADD_CONTENT_ID}`)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        void openProductSizeGuide(root, openControl);
        return;
      }

      const guide = root.querySelector(`#${SIZE_GUIDE_ID}[data-vz-quick-add-shared-guide]`);
      if (!(guide instanceof HTMLDialogElement)) return;

      if (event.target.closest('[data-vz-size-close]') || event.target === guide) {
        event.preventDefault();
        guide.close();
      }
    },
    true
  );

  const outerDialog = root.querySelector(':scope > dialog');
  outerDialog?.addEventListener('close', releaseActiveGuide);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindQuickAddSizeGuide, { once: true });
} else {
  bindQuickAddSizeGuide();
}
